import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhoneAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import logoImage from '../assets/logo.jpg';

const NavContainer = styled.header`
  background: ${props => props.$scrolled 
    ? 'var(--background-accent)' 
    : 'linear-gradient(to right, var(--background-accent) 0%, var(--background-lighter) 50%, var(--background-accent) 100%)'};
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-bottom: 4px solid var(--primary-color);
`;

const NavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const LogoImage = styled.img`
  height: 60px;
  width: 60px;
  margin-right: 10px;
  object-fit: cover;
  transition: var(--transition);
  border-radius: 50%;
  background: white;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }
  
  @media (max-width: 768px) {
    height: 50px;
    width: 50px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  
  &:hover {
    color: var(--text-primary);
    
    ${LogoImage} {
      transform: scale(1.05);
    }
  }
`;

const MenuIcon = styled.div`
  display: none;
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavMenu = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 75%;
  max-width: 320px;
  height: 100%;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--background-lighter) 0%, var(--background-accent) 100%);
  padding: 2rem 1.5rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  border-left: 3px solid var(--primary-color);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: radial-gradient(circle at top right, rgba(212, 175, 55, 0.1), transparent);
    pointer-events: none;
  }
  
  .close-icon {
    align-self: flex-end;
    font-size: 1.8rem;
    margin-bottom: 2.5rem;
    cursor: pointer;
    color: var(--text-primary);
    background: var(--background-lighter);
    width: 45px;
    height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 1;
    
    &:hover {
      background: var(--primary-color);
      color: white;
      transform: rotate(90deg);
    }
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9998;
`;

const NavItem = styled(Link)`
  color: ${props => props.$isActive ? 'var(--primary-color)' : 'var(--text-primary)'};
  margin: 0 1rem;
  font-weight: 500;
  position: relative;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${props => props.$isActive ? '100%' : '0'};
    height: 2px;
    background-color: var(--primary-color);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: var(--primary-color);
    
    &:after {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    margin: 0.8rem 0;
    font-size: 1.1rem;
    padding: 14px 20px;
    border-radius: 12px;
    background: ${props => props.$isActive ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : 'var(--background-lighter)'};
    color: ${props => props.$isActive ? 'white' : 'var(--text-primary)'};
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    z-index: 1;
    
    &:after {
      display: none;
    }
    
    &:hover {
      background: ${props => props.$isActive ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : 'var(--accent-color)'};
      color: ${props => props.$isActive ? 'white' : 'var(--primary-color)'};
      transform: translateX(8px);
      box-shadow: 0 4px 16px rgba(212, 175, 55, 0.2);
    }
  }
`;

const ContactButton = styled(Link)`
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  margin-left: 1rem;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover::before {
    width: 300px;
    height: 300px;
  }
  
  svg {
    margin-right: 6px;
    position: relative;
    z-index: 1;
  }
  
  &:hover {
    background-color: var(--secondary-color);
    color: white;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin: 1.5rem 0 0;
    justify-content: center;
  }
`;

const AuthButton = styled(Link)`
  display: flex;
  align-items: center;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);
  margin-left: 1rem;
  font-weight: 500;
  transition: var(--transition);
  
  &:hover {
    background-color: var(--secondary-color);
    color: white;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin: 1rem 0;
    justify-content: center;
  }
`;

const menuVariants = {
  hidden: { x: '100%' },
  visible: { 
    x: 0,
    transition: {
      type: 'tween',
      duration: 0.3
    }
  },
  exit: { 
    x: '100%',
    transition: {
      type: 'tween',
      duration: 0.3
    }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Prevent body scroll when menu opens
    if (!mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  };
  
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    // Re-enable body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <NavContainer $scrolled={scrolled}>
      <NavWrapper>
        <Logo to="/">
          <LogoImage src={logoImage} alt="Istri Wala Logo" />
          Istri Wala
        </Logo>
        
        <MenuIcon onClick={toggleMobileMenu}>
          <FaBars />
        </MenuIcon>
        
        <NavMenu>
          <NavItem to="/" $isActive={isActive('/')}>Home</NavItem>
          <NavItem to="/services" $isActive={isActive('/services')}>Services</NavItem>
          <NavItem to="/about" $isActive={isActive('/about')}>About</NavItem>
          <NavItem to="/pricing" $isActive={isActive('/pricing')}>Pricing</NavItem>
          <ContactButton to="/contact">
            <FaPhoneAlt />
            Contact Us
          </ContactButton>
          {user ? (
            <AuthButton to="/profile">My Profile</AuthButton>
          ) : (
            <AuthButton to="/login">Login</AuthButton>
          )}
        </NavMenu>
        
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              <Overlay 
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={closeMobileMenu}
              />
              <MobileMenu
                variants={menuVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="close-icon" onClick={closeMobileMenu}>
                  <FaTimes />
                </div>
                <Logo to="/" onClick={closeMobileMenu} style={{ marginBottom: '20px' }}>
                  <LogoImage src={logoImage} alt="Istri Wala Logo" />
                  Istri Wala
                </Logo>
                <NavItem to="/" $isActive={isActive('/')} onClick={closeMobileMenu}>Home</NavItem>
                <NavItem to="/services" $isActive={isActive('/services')} onClick={closeMobileMenu}>Services</NavItem>
                <NavItem to="/about" $isActive={isActive('/about')} onClick={closeMobileMenu}>About</NavItem>
                <NavItem to="/pricing" $isActive={isActive('/pricing')} onClick={closeMobileMenu}>Pricing</NavItem>
                <ContactButton to="/contact" onClick={closeMobileMenu}>
                  <FaPhoneAlt />
                  Contact Us
                </ContactButton>
                {user ? (
                  <AuthButton to="/profile" onClick={closeMobileMenu}>My Profile</AuthButton>
                ) : (
                  <AuthButton to="/login" onClick={closeMobileMenu}>Login</AuthButton>
                )}
              </MobileMenu>
            </>
          )}
        </AnimatePresence>
      </NavWrapper>
    </NavContainer>
  );
};

export default Navbar;
